import { refreshWelcomeSuggestions } from './welcome-suggestions'
import { canEnterConversation, conversationAvailable } from './conversation-entry-policy'
import { CAPABILITIES, validateSnapshot } from '@/ai/protocol/contract'
import { errorMessage, identity } from '@/ai/services/api'

// 方法绑定到原页面实例；不持有独立状态、请求实例或生命周期。
export default {
  toggleHistory() {
    if (!this.showHistory && !canEnterConversation(this)) return
    if (this.busy) return
    this.quotaOpen = false
    this.settingsOpen = false
    this.showHistory = !this.showHistory
    if (this.showHistory) {
      this.loadSessions()
      this.$nextTick(() => { const close = this.$el.querySelector('.ai-history'); if (close) close.focus() })
    } else this.$nextTick(() => { const trigger = this.$el.querySelector('[aria-label="历史对话"]'); if (trigger) trigger.focus() })
  },
  resumeKey() {
    return (
      'aiSession:v2:' + (sessionStorage.getItem('aiLoginEpoch') || '') + ':' +
      identity()
        .split('|')
        .slice(0, 2)
        .join('|')
    )
  },
  loadSessionsNearBottom() {
    const box = this.$refs.sessionList
    if (this.showHistory && box && box.clientHeight > 0 && !this.sessionsError && this.sessionsCursor &&
        box.scrollHeight - box.scrollTop - box.clientHeight < 80) this.loadSessions(true)
  },
  async loadSessions(more = false) {
    if (!this.caps.enabled || this.sessionsLoading || (more && !this.sessionsCursor)) return
    if (!more && this.sessionsLoaded) {
      this.$nextTick(this.loadSessionsNearBottom)
      return
    }
    this.sessionsLoading = true
    this.sessionsError = ''
    try {
      const cursor = more ? this.sessionsCursor : null
      const result = await this.call('session/list', { pageSize: 20, ...(cursor ? { cursor } : {}) })
      const existing = more ? this.sessionList : []
      const known = new Set(existing.map(item => item.sessionId))
      this.sessionList = existing.concat(result.items.filter(item => !known.has(item.sessionId)))
      this.sessionsCursor = result.nextCursor && result.nextCursor !== cursor ? result.nextCursor : null
      this.sessionsLoaded = true
    } catch (error) {
      this.sessionsError = errorMessage(error)
    } finally {
      this.sessionsLoading = false
      this.$nextTick(this.loadSessionsNearBottom)
    }
  },
  historyNotice() {
    return this.session && this.session.readOnlyNotice || '无法继续对话。';
  },
  async newConversation() {
    if (!this._alive || !canEnterConversation(this)) return false
    const closedSessionId = this.session ? this.session.sessionId : null
    this.sessionChanging = true
    try {
      const closeBody = (this.session
        ? { sessionId: this.session.sessionId }
        : this.sessionRequest ? { sessionRequest: this.sessionRequest } : null)
      if (closeBody) {
        const closed = await this.call('session/close', closeBody)
        if (!closed || closed.state !== 'CLOSED') throw new Error('INVALID_CLOSE_RESPONSE')
      }
      if (!this._alive) return
      this._conversationEpoch = (this._conversationEpoch || 0) + 1
      clearTimeout(this._poll)
      clearTimeout(this._expiryTimer)
      this._pollEpoch = (this._pollEpoch || 0) + 1
      sessionStorage.removeItem(this.resumeKey())
      this.session = null
      this.turns = []
      this.pendingRequest = null; this.pendingSpeech = null
      this.sessionRequest = null
      this.errorCode = ''
      this.historyMode = false
      this.historyCursor = null
      this.historyPaged = false
      this.historyError = false
      this.speechNotice = ''
      this._pollError = null; this._pollFailures = 0; this._pollDeadline = null
      this.error = ''
      this.draft = ''; this.speechRefs = []; this.speechMixed = false
      this.sessionList = this.sessionList.map(item => item.sessionId === closedSessionId ? { ...item, state: 'CLOSED' } : item)
      refreshWelcomeSuggestions(this)
      this.$toast('已新建对话', 1000)
      return true
    } catch (error) {
      this.fail(error)
      if (this._alive && !error.silent) this.$toast('新建对话失败，请重试', 1500)
      return false
    } finally {
      this.sessionChanging = false
    }
  },
  async openHistory(item) {
    if (!this._alive || !canEnterConversation(this)) return false
    this.sessionChanging = true
    try {
      let result = await this.call('session/get', {
        sessionId: item.sessionId, direction: 'older'
      })
      if (!this._alive || !conversationAvailable(this) || this.speechActive) return false
      const canResume = result.canResume === true
      // The resume endpoint rechecks expiry/login under lock; never infer permission in the UI.
      if (canResume) result = await this.call('session/resume', { sessionId: item.sessionId, capabilities: CAPABILITIES })
      if (!this._alive || !conversationAvailable(this) || this.speechActive) return false
      const rows = result.items.map(t => ({
        ...t,
        blocked: validateSnapshot(t).blocked
      }))
      clearTimeout(this._poll)
      clearTimeout(this._expiryTimer)
      this._pollEpoch = (this._pollEpoch || 0) + 1
      this._conversationEpoch = (this._conversationEpoch || 0) + 1
      this.historyMode = !canResume
      this.showHistory = false
      this.session = result
      if (canResume) sessionStorage.setItem(this.resumeKey(), result.sessionId)
      else sessionStorage.removeItem(this.resumeKey())
      this.turns = rows
      this.historyCursor = result.nextCursor
      this.historyPaged = false
      this.historyError = false
      this.$nextTick(this.scrollToLatest)
      this.armExpiry()
      if (canResume && this.latest) this.schedule(this.latest)
      this.draft = ''; this.speechRefs = []; this.speechMixed = false
      this.sessionRequest = null
      this.pendingRequest = null; this.pendingSpeech = null
      this.error = ''
      return true
    } catch (error) {
      this.fail(error)
      return false
    } finally {
      this.sessionChanging = false
    }
  },
  async moreHistory() {
    if (this.historyLoading || this.sessionChanging || !this.session || !this.historyCursor) return
    const sessionId = this.session.sessionId
    const session = this.session
    const cursor = this.historyCursor
    this.historyLoading = true
    this.historyError = false
    try {
      const result = await this.call('session/get', { sessionId, cursor, direction: 'older' })
      if (!this._alive || this.session !== session || this.historyCursor !== cursor) return
      const box = this.$refs.scroll
      const height = box ? box.scrollHeight : 0
      const top = box ? box.scrollTop : 0
      const known = new Set(this.turns.map(t => t.turnId))
      const rows = result.items.filter(t => !known.has(t.turnId)).map(t => ({ ...t, blocked: validateSnapshot(t).blocked }))
      this.turns = rows.concat(this.turns)
      this.historyCursor = result.nextCursor
      this.historyPaged = true
      await this.$nextTick()
      if (box && this.session === session) box.scrollTop = top + box.scrollHeight - height
    } catch (error) {
      if (this.session === session) this.historyError = true
    } finally {
      this.historyLoading = false
    }
  },
  resolveDelete(confirmed) {
    const pending = this._deleteResolve
    this._deleteResolve = null
    this.deleteDialog = null
    if (pending) pending(confirmed)
  },
  async removeSession(item) {
    if (this.busy || this.deleteDialog || !this._alive) return
    const confirmed = await new Promise(resolve => {
      this._deleteResolve = resolve
      this.deleteDialog = { message: this.pageState.copy.deleteNotice || '删除这段对话？' }
    })
    if (!confirmed || !this._alive || this.busy) return
    this.sessionChanging = true
    try {
      await this.call('session/delete', { sessionId: item.sessionId })
      if (this.session && item.sessionId === this.session.sessionId) {
        clearTimeout(this._poll)
        clearTimeout(this._expiryTimer)
        this._pollEpoch = (this._pollEpoch || 0) + 1
        sessionStorage.removeItem(this.resumeKey())
        this.session = null
        this.turns = []
        this.historyMode = false
        this.historyCursor = null
        this.historyPaged = false
        this.pendingRequest = null; this.pendingSpeech = null
        this.sessionRequest = null
      }
      this.sessionList = this.sessionList.filter(row => row.sessionId !== item.sessionId)
    } catch (error) {
      this.fail(error)
    } finally {
      this.sessionChanging = false
    }
  }
}
